/* eslint-disable @typescript-eslint/no-floating-promises */

import { Location } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { MatStepper } from '@angular/material/stepper';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, firstValueFrom } from 'rxjs';
import { AuthorizationService } from 'services/authorization.service';
import { CentralServerService } from 'services/central-server.service';
import { LanguageService } from 'services/language.service';
import { MessageService } from 'services/message.service';
import { SpinnerService } from 'services/spinner.service';
import { WindowService } from 'services/window.service';
import { AbstractTabComponent } from 'shared/component/abstract-tab/abstract-tab.component';
import { ChargingStation, Connector, ConnectorType, QrCodeData } from 'types/ChargingStation';
import { UserNotifications } from 'types/User';
import { Utils } from 'utils/Utils';

import { SessionService } from './service/session.service';

@Component({
  selector: 'app-qrcode',
  templateUrl: 'qrcode.component.html',
  styleUrls: ['qrcode.component.scss'],
})
export class QrCodeComponent extends AbstractTabComponent implements OnInit, OnDestroy {
  @ViewChild('stepper') stepper!: MatStepper;
  @ViewChild('otpInput') otpInput?: ElementRef<HTMLInputElement>;

  step1Form: FormGroup;
  step2Form: FormGroup;
  step3Form: FormGroup;

  public pricingDefinitions: any | null = null;
  public chargingStation: ChargingStation | null = null;
  public connectorId: number;
  public connectorExists = true;
  public qrDataLoading = true;
  public stationLoaded = false;
  public connectorStatus: string | null = null;
  public browserLanguage: string;
  public selectedLanguage = 'fa';
  public connectorStatusColor: string;
  public chargingStationId: string;
  public connectorTest: Connector;
  public tenantLogo: string | null = null;
  public tenantName = '';
  codedConnectorID: string;
  QrCodeData: QrCodeData;
  codedChargingStationID: string;
  intervalId: any;
  isLoggedIn = false;
  token: any;
  dataLoaded = false;

  currentStep = 0;
  isProcessing = false;
  isRedirectingToTamin = false;
  readonly minimumWalletBalance = 50000;
  quickAmounts = [50000, 100000, 200000, 500000, 1000000];
  currentWalletBalance = 0;
  estimatedSessionCost = 0;

  otpFlow = '';
  otpSent = false;
  verificationToken = '';
  isSendingOTP = false;
  isVerifyingOTP = false;
  otpResendCooldown = 0;
  private otpAutoFillAbortController?: AbortController;
  private otpResendIntervalId?: ReturnType<typeof setInterval>;

  public constructor(
    activatedRoute: ActivatedRoute,
    windowService: WindowService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private fb: FormBuilder,
    private centralServerService: CentralServerService,
    private spinnerService: SpinnerService,
    private languageService: LanguageService,
    private translateService: TranslateService,
    private authorizationService: AuthorizationService,
    private messageService: MessageService,
    private sessionService: SessionService
  ) {
    super(activatedRoute, windowService, ['all']);
    this.initForms();
  }

  get hasFlatFee(): boolean {
    return this.pricingDefinitions?.dimensions?.flatFee?.active === true && this.pricingDefinitions?.dimensions?.flatFee?.price != null;
  }
  get hasEnergy(): boolean {
    return this.pricingDefinitions?.dimensions?.energy?.active === true && this.pricingDefinitions?.dimensions?.energy?.price != null;
  }
  get hasChargingTime(): boolean {
    return this.pricingDefinitions?.dimensions?.chargingTime?.active === true && this.pricingDefinitions?.dimensions?.chargingTime?.price != null;
  }
  get hasParkingTime(): boolean {
    return this.pricingDefinitions?.dimensions?.parkingTime?.active === true && this.pricingDefinitions?.dimensions?.parkingTime?.price != null;
  }

  get requiresWalletTopUp(): boolean {
    return (this.step2Form.get('creditAmount')?.value || 0) > 0;
  }

  get walletMinBalanceMessage(): string {
    const formatted = this.formatIRR(this.minimumWalletBalance);
    return this.translateService.instant('qrcode.wallet_min_balance', { amount: formatted });
  }

  get siteName(): string {
    return this.chargingStation?.siteArea?.name || this.chargingStation?.site?.name || '';
  }

  get siteAddress(): string {
    return this.chargingStation?.site?.address?.address1 || '';
  }

  get connectorTypeLabel(): string {
    const connector = this.chargingStation?.connectors?.find(c => c.connectorId === this.connectorId);
    if (!connector) {
      return '';
    }
    const typeLabels: Record<string, string> = {
      [ConnectorType.TYPE_2]: 'Type 2',
      [ConnectorType.COMBO_CCS]: 'CCS',
      [ConnectorType.CHADEMO]: 'CHAdeMO',
      [ConnectorType.TYPE_1]: 'Type 1',
      [ConnectorType.TYPE_3C]: 'Type 3C',
      [ConnectorType.DOMESTIC]: 'Domestique',
      [ConnectorType.GBT_AC]: 'GBT AC',
      [ConnectorType.GBT_DC]: 'GBT DC',
      [ConnectorType.UNKNOWN]: 'Inconnu',
    };
    const typeLabel = typeLabels[connector.type] || connector.type;
    const power = connector.power ? `${Math.round(connector.power / 1000)}kW` : '';
    return power ? `${typeLabel} - ${power}` : typeLabel;
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    this.cancelOtpAutoFill();
    this.clearOtpResendCooldown();
  }

  async ngOnInit(): Promise<void> {
    this.chargingStation = null;
    this.pricingDefinitions = null;
    this.setupLanguagePreferences();
    this.loadCachedContact();
    this.route.paramMap.subscribe((params) => {
      this.codedChargingStationID = params.get('codedChargingStationID');
      this.codedConnectorID = params.get('codedConnectorID');
    });
    if (this.codedChargingStationID && this.codedConnectorID) {
      try {
        this.QrCodeData = await firstValueFrom(this.centralServerService.getQrCodeData(this.codedChargingStationID, this.codedConnectorID));
        this.token = this.QrCodeData.token;
        this.chargingStationId = this.QrCodeData.chargingStationID;
        this.connectorId = Number(this.QrCodeData.connectorID);
        this.tenantName = this.QrCodeData.tenantName || '';
        if (this.tenantName) {
          this.centralServerService.getTenantLogoBySubdomain(this.tenantName).subscribe({
            next: (logo) => this.tenantLogo = logo,
            error: () => this.tenantLogo = null,
          });
        }
        this.qrDataLoading = false;
        setTimeout(() => this.checkLoginAndLoadData(), 2000);
      } catch (error) {
        console.error('Error fetching QR code data:', error);
        this.qrDataLoading = false;
        this.connectorExists = false;
      }
    } else {
      console.error('codedChargingStationID or codedConnectorID is missing');
      this.qrDataLoading = false;
      this.connectorExists = false;
    }

    this.route.queryParams.subscribe(async (params) => {
      if (params['requestId']) {
        await this.verifyPaymentAndStartSession(params['requestId']);
      }
    });

    const pendingRequestId = sessionStorage.getItem('qr_pending_requestId');
    if (pendingRequestId) {
      sessionStorage.removeItem('qr_pending_requestId');
      await this.verifyPaymentAndStartSession(pendingRequestId);
    }
  }

  async nextStep(): Promise<void> {
    if (this.currentStep === 0) {
      if (!this.otpSent) {
        if (!await this.refreshAndCheckConnectorStatus()) {
          return;
        }
        await this.sendOTP();
        return;
      }
      if (!await this.refreshAndCheckConnectorStatus()) {
        return;
      }
      const success = await this.verifyAndLogin();
      if (!success) {
        return;
      }
      await this.loadWalletBalance();
      if (this.currentWalletBalance >= this.minimumWalletBalance) {
        this.step2Form.get('creditAmount')?.setValue(0);
        const step1 = this.stepper.steps.get(1);
        if (step1) {
          step1.completed = true;
        }
        this.stepper.selectedIndex = 2;
        return;
      }
    }
    if (this.currentStep === 1) {
      if (!await this.refreshAndCheckConnectorStatus()) {
        return;
      }
      if (this.currentWalletBalance >= this.minimumWalletBalance && !this.requiresWalletTopUp) {
        if (this.stepper) {
          this.stepper.next();
        }
        return;
      }
      const creditAmount = this.step2Form.get('creditAmount')?.value || 0;
      if (creditAmount <= 0) {
        this.messageService.showErrorMessage(this.walletMinBalanceMessage);
        return;
      }
    }
    if (this.stepper) {
      this.stepper.next();
    }
  }

  prevStep(): void {
    if (this.stepper) {
      this.stepper.previous();
    }
  }

  onStepChange(event: any): void {
    this.currentStep = event.selectedIndex;
    if (this.currentStep === 1) {
      this.loadWalletBalance();
    }
  }

  setQuickAmount(amount: number): void {
    this.step2Form.get('creditAmount')?.setValue(amount);
  }

  public cancelOtpAutoFill(): void {
    this.otpAutoFillAbortController?.abort();
    this.otpAutoFillAbortController = undefined;
  }

  public async resendOTP(): Promise<void> {
    const phone = this.step1Form.get('phone')?.value;
    if (!phone || this.otpResendCooldown > 0 || this.isSendingOTP
      || (this.otpFlow !== 'LOGIN' && this.otpFlow !== 'REGISTRATION')) {
      return;
    }

    this.isSendingOTP = true;
    this.startOtpAutoFill();
    try {
      await firstValueFrom(this.centralServerService.resendOTP(
        phone,
        this.otpFlow,
        this.otpFlow === 'REGISTRATION' ? this.verificationToken : undefined
      ));
      this.step1Form.get('otp')?.reset();
      this.startOtpResendCooldown();
      this.focusOtpInput();
      this.messageService.showInfoMessage(this.translateService.instant('qrcode.otp_resent'));
    } catch (error) {
      this.cancelOtpAutoFill();
      console.error('Error resending OTP:', error);
      this.messageService.showErrorMessage(this.translateService.instant('qrcode.otp_send_error'));
    } finally {
      this.isSendingOTP = false;
    }
  }

  async loadWalletBalance(): Promise<void> {
    try {
      const userId = this.sessionService.getUserId();
      if (userId) {
        const wallet = await firstValueFrom(this.centralServerService.getQRCodeWalletBalance(userId));
        this.currentWalletBalance = wallet?.credit || 0;
      }
    } catch (error) {
      console.error('Error loading wallet:', error);
    }
  }

  async refreshAndCheckConnectorStatus(): Promise<boolean> {
    try {
      const station = await firstValueFrom(this.loadChargingStation(this.chargingStationId, this.connectorId));
      const connector = Utils.getConnectorFromID(station, this.connectorId);
      const status = connector?.status;
      if (status !== 'Preparing') {
        this.messageService.showErrorMessage(this.getConnectorNotReadyMessage(status));
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error refreshing connector status:', error);
      this.messageService.showErrorMessage(this.getConnectorNotReadyMessage());
      return false;
    }
  }

  changeLanguage(lang: string): void {
    this.selectedLanguage = lang;
    this.translateService.use(lang);
    sessionStorage.setItem('qr_selected_language', lang);
    document.documentElement.dir = lang === 'fa' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }

  roundPrice(price: number): number {
    return Math.round(price);
  }

  formatIRR(value: number): string {
    return Math.round(value).toLocaleString('fr-FR').replace(/\s/g, '.');
  }

  calculateEstimatedCost(): void {
    if (this.pricingDefinitions?.dimensions?.energy?.price) {
      const powerKw = this.chargingStation?.connectors?.find(c => c.connectorId === this.connectorId)?.power || 22;
      this.estimatedSessionCost = (powerKw / 1000) * this.pricingDefinitions.dimensions.energy.price;
    }
  }

  getPaymentMethodLabel(): string {
    return this.translateService.instant('qrcode.wallet');
  }

  goBack(): void {
    this.location.back();
  }

  public getChargingStationTariffID(): void {
    const ChargingStationID = this.chargingStation?.id;
    const ConnectorID = this.connectorId;
    const roaming = false;
    if (ChargingStationID && ConnectorID) {
      this.centralServerService.getChargingStationTariffQr(ChargingStationID, ConnectorID, roaming).subscribe({
        next: (data) => {
          this.pricingDefinitions = data;
        },
        error: (err) => console.error('Error fetching tariff data:', err),
      });
    }
  }

  public createAllTrueNotifications = (): UserNotifications => ({
    sendSessionStarted: true, sendOptimalChargeReached: true, sendEndOfCharge: true, sendEndOfSession: true,
    sendUserAccountStatusChanged: true, sendNewRegisteredUser: true, sendUnknownUserBadged: true,
    sendChargingStationStatusError: true, sendChargingStationRegistered: true, sendOcpiPatchStatusError: true,
    sendOicpPatchStatusError: true, sendUserAccountInactivity: true, sendPreparingSessionNotStarted: true,
    sendOfflineChargingStations: true, sendBillingSynchronizationFailed: true, sendBillingPeriodicOperationFailed: true,
    sendSessionNotStarted: true, sendCarCatalogSynchronizationFailed: true, sendComputeAndApplyChargingProfilesFailed: true,
    sendEndUserErrorNotification: true, sendBillingNewInvoice: true, sendAdminAccountVerificationNotification: true,
  });

  checkLoginAndLoadData(): void {
    this.authorizationService.getUserRole();
    this.setupLanguagePreferences();
    const entityId = this.chargingStationId;
    const connectorId = this.connectorId;
    if (entityId) {
      this.loadChargingStation(entityId, connectorId).subscribe({
        next: () => {
          this.getChargingStationTariffID();
          if (connectorId) {
            this.intervalId = setInterval(() => this.loadChargingStation(entityId, connectorId).subscribe(), 5000);
          }
        },
        error: (error) => console.error('Failed to load charging station:', error),
      });
    }
  }

  async startSession(): Promise<void> {
    if (this.step3Form.invalid || this.isProcessing) {
      return;
    }

    if (!await this.refreshAndCheckConnectorStatus()) {
      this.messageService.showErrorMessage(this.translateService.instant('qrcode.connector_not_ready_redirect'));
      this.resetToStep0();
      return;
    }

    this.isProcessing = true;
    this.spinnerService.show();
    try {
      const userId = this.sessionService.getUserId();
      if (!userId) {
        this.messageService.showErrorMessage(this.translateService.instant('qrcode.session_start_failed'));
        return;
      }

      const creditAmount = this.step2Form.get('creditAmount')?.value || 0;

      if (creditAmount > 0) {
        this.isRedirectingToTamin = true;
        const callbackUrl = `${window.location.origin}/qrcode/${this.codedChargingStationID}/${this.codedConnectorID}`;
        const result = await firstValueFrom(this.centralServerService.addQRCodeWalletCredit({
          userID: userId,
          amount: creditAmount,
          callbackUrl,
        }));
        if (result?.gatewayUrl) {
          sessionStorage.setItem('qr_session_userId', userId);
          sessionStorage.setItem('qr_pending_requestId', result.requestId);
          window.location.href = result.gatewayUrl;
          return;
        }
        this.messageService.showErrorMessage(this.translateService.instant('qrcode.wallet_charge_error'));
        this.isRedirectingToTamin = false;
        return;
      }

      sessionStorage.setItem('qr_session_userId', userId);
      this.router.navigate([`/qrcode/${this.codedChargingStationID}/${this.codedConnectorID}/charging`]);
    } catch (error) {
      console.error('Error starting session:', error);
      this.messageService.showErrorMessage(this.translateService.instant('qrcode.session_start_failed'));
    } finally {
      this.isProcessing = false;
      this.spinnerService.hide();
    }
  }

  private getConnectorNotReadyMessage(status?: string): string {
    if (status === 'Available') {
      const plugMsg = this.translateService.instant('qrcode.plug_vehicle');
      return plugMsg !== 'qrcode.plug_vehicle' ? plugMsg : 'Veuillez brancher votre véhicule à la borne pour continuer.';
    }
    const unavailMsg = this.translateService.instant('qrcode.station_unavailable');
    return unavailMsg !== 'qrcode.station_unavailable' ? unavailMsg : 'Borne non disponible actuellement !';
  }

  private resetToStep0(): void {
    this.cancelOtpAutoFill();
    this.clearOtpResendCooldown();
    this.otpSent = false;
    this.otpFlow = '';
    this.verificationToken = '';
    this.currentStep = 0;
    this.step1Form.get('otp')?.reset();
    this.step1Form.get('otp')?.clearValidators();
    this.step1Form.get('otp')?.updateValueAndValidity();
    if (this.stepper) {
      this.stepper.selectedIndex = 0;
      this.stepper.steps.forEach((step, index) => {
        if (index > 0) {
          step.completed = false;
        }
      });
    }
  }

  private loadCachedContact(): void {
    const cachedPhone = localStorage.getItem('qr_cached_phone');
    const cachedEmail = localStorage.getItem('qr_cached_email');
    if (cachedPhone) {
      this.step1Form.get('phone')?.setValue(cachedPhone);
    }
    if (cachedEmail) {
      this.step1Form.get('email')?.setValue(cachedEmail);
    }
  }

  private saveCachedContact(): void {
    const phone = this.step1Form.get('phone')?.value;
    const email = this.step1Form.get('email')?.value;
    if (phone) {
      localStorage.setItem('qr_cached_phone', phone);
    }
    if (email) {
      localStorage.setItem('qr_cached_email', email);
    }
  }

  private initForms(): void {
    this.step1Form = this.fb.group({
      phone: ['', [Validators.required, Validators.pattern(/^09[0-9]{9}$/)]],
      email: ['', [Validators.email]],
      otp: ['', [Validators.pattern(/^\d{4}$/)]],
    });

    this.step2Form = this.fb.group({
      creditAmount: [100000, [Validators.required, Validators.min(50000), Validators.max(500000000)]]
    });

    this.step3Form = this.fb.group({
      acceptTerms: [true]
    });
  }

  private async sendOTP(): Promise<void> {
    const phone = this.step1Form.get('phone')?.value;
    if (!phone) {
      return;
    }
    this.isSendingOTP = true;
    this.spinnerService.show();
    this.startOtpAutoFill();
    try {
      const result = await firstValueFrom(this.centralServerService.qrOtpInit(phone));
      this.otpFlow = result.flow;
      this.otpSent = true;
      this.saveCachedContact();
      if (this.otpFlow === 'REGISTRATION') {
        const email = this.step1Form.get('email')?.value || `${phone}@${this.tenantName}.cpo`;
        const password = Utils.generateTagID() + 'a!';
        const regResult = await firstValueFrom(this.centralServerService.qrRegisterMobileUser({
          phone, firstName: phone, name: phone, email, password, acceptEula: true,
        }));
        this.verificationToken = regResult.verificationToken;
        this.step1Form.get('otp')?.setValidators([Validators.required, Validators.pattern(/^\d{4}$/)]);
        this.step1Form.get('otp')?.updateValueAndValidity();
        this.messageService.showInfoMessage(this.translateService.instant('qrcode.otp_sent'));
      } else if (this.otpFlow === 'LOGIN') {
        this.step1Form.get('otp')?.setValidators([Validators.required, Validators.pattern(/^\d{4}$/)]);
        this.step1Form.get('otp')?.updateValueAndValidity();
        this.messageService.showInfoMessage(this.translateService.instant('qrcode.otp_sent'));
      } else if (this.otpFlow === 'ACCOUNT_STATUS' || this.otpFlow === 'LINK_ACCOUNT') {
        this.messageService.showErrorMessage(result.message || this.translateService.instant('qrcode.otp_send_error'));
        this.otpSent = false;
        this.cancelOtpAutoFill();
      }
      if (this.otpSent) {
        this.startOtpResendCooldown();
        this.focusOtpInput();
      }
    } catch (error) {
      this.cancelOtpAutoFill();
      console.error('Error sending OTP:', error);
      this.messageService.showErrorMessage(this.translateService.instant('qrcode.otp_send_error'));
    } finally {
      this.isSendingOTP = false;
      this.spinnerService.hide();
    }
  }

  private async verifyAndLogin(): Promise<boolean> {
    const phone = this.step1Form.get('phone')?.value;
    const code = this.step1Form.get('otp')?.value;
    if (!phone || !code) {
      return false;
    }
    this.cancelOtpAutoFill();
    this.isVerifyingOTP = true;
    this.spinnerService.show();
    try {
      if (this.otpFlow === 'LOGIN') {
        const result = await firstValueFrom(this.centralServerService.phoneLogin(phone, code));
        this.centralServerService.loginSucceeded(result.token);
        this.storeQRCodeUserSession(result.token);
        return true;
      } else if (this.otpFlow === 'REGISTRATION') {
        const verifyResult = await firstValueFrom(this.centralServerService.qrVerifyRegistration(phone, code, this.verificationToken));
        this.centralServerService.loginSucceeded(verifyResult.token);
        this.storeQRCodeUserSession(verifyResult.token);
        return true;
      }
      return false;
    } catch (error: any) {
      console.error('OTP verification error:', error);
      this.messageService.showErrorMessage(this.translateService.instant('qrcode.otp_verify_error'));
      return false;
    } finally {
      this.isVerifyingOTP = false;
      this.spinnerService.hide();
    }
  }

  private storeQRCodeUserSession(token: string): void {
    const user = new JwtHelperService().decodeToken(token);
    const userId = user?.id;
    if (!userId) {
      throw new Error('QR authentication token does not contain a user ID');
    }
    this.sessionService.setUserId(userId);
    this.sessionService.setQRCodeUser(user);
    sessionStorage.setItem('qr_session_userId', userId);
  }

  private startOtpAutoFill(): void {
    this.cancelOtpAutoFill();
    if (typeof window === 'undefined' || !('OTPCredential' in window) || !navigator.credentials) {
      return;
    }

    const abortController = new AbortController();
    this.otpAutoFillAbortController = abortController;
    const options = {
      otp: { transport: ['sms'] },
      signal: abortController.signal,
    } as unknown as CredentialRequestOptions;

    void navigator.credentials.get(options).then((credential) => {
      if (this.otpAutoFillAbortController !== abortController) {
        return;
      }
      this.otpAutoFillAbortController = undefined;
      const code = (credential as unknown as { code?: string } | null)?.code;
      if (code && /^\d{4}$/.test(code)) {
        const otpControl = this.step1Form.get('otp');
        otpControl?.setValue(code);
        otpControl?.markAsDirty();
        otpControl?.markAsTouched();
        otpControl?.updateValueAndValidity();
      }
    }).catch(() => {
      // Unsupported browsers and dismissed OTP prompts use the normal manual entry flow.
    });
  }

  private focusOtpInput(): void {
    setTimeout(() => this.otpInput?.nativeElement.focus());
  }

  private startOtpResendCooldown(): void {
    this.clearOtpResendCooldown();
    this.otpResendCooldown = 60;
    this.otpResendIntervalId = setInterval(() => {
      this.otpResendCooldown--;
      if (this.otpResendCooldown === 0) {
        this.clearOtpResendCooldown();
      }
    }, 1000);
  }

  private clearOtpResendCooldown(): void {
    if (this.otpResendIntervalId !== undefined) {
      clearInterval(this.otpResendIntervalId);
      this.otpResendIntervalId = undefined;
    }
    this.otpResendCooldown = 0;
  }

  private async verifyPaymentAndStartSession(requestId: string): Promise<void> {
    this.spinnerService.show();
    try {
      const verification = await firstValueFrom(this.centralServerService.verifyPayment(requestId));
      if (!verification?.success || verification.status !== 'paid') {
        this.messageService.showErrorMessage(this.translateService.instant('qrcode.wallet_charge_pending'));
        return;
      }
      this.messageService.showSuccessMessage(this.translateService.instant('qrcode.wallet_charge_success'));
      this.router.navigate([`/qrcode/${this.codedChargingStationID}/${this.codedConnectorID}/charging`]);
    } catch (error) {
      console.error('Error verifying payment:', error);
      this.messageService.showErrorMessage(this.translateService.instant('qrcode.wallet_charge_error'));
    } finally {
      this.spinnerService.hide();
    }
  }

  private loadChargingStation(entityId: string, connectorId: number): Observable<ChargingStation> {
    return new Observable((observer) => {
      this.centralServerService.getChargingStationQr(entityId).subscribe({
        next: (chargingStation: ChargingStation) => {
          this.chargingStation = chargingStation;
          this.connectorExists = chargingStation.connectors.some((c) => c.connectorId === connectorId);
          this.connectorId = connectorId;
          this.connectorTest = Utils.getConnectorFromID(chargingStation, connectorId);
          if (this.connectorTest) {
            this.connectorStatus = this.connectorTest.status;
            const statusKey = `connectorStatus.${this.connectorTest.status.toUpperCase()}` || this.connectorTest.status;
            this.translateService.get(statusKey).subscribe((translatedStatus: string) => {
              this.connectorStatusColor = Utils.applyStatusColor(this.connectorTest.status.toUpperCase());
              this.connectorExists = true;
              this.stationLoaded = true;
              this.dataLoaded = true;
              observer.next(chargingStation);
              observer.complete();
            });
          } else {
            this.connectorExists = false;
            this.connectorStatus = '';
            this.stationLoaded = true;
            this.dataLoaded = false;
            observer.error('Connector does not exist');
          }
          if (!this.connectorExists) {
            this.spinnerService.hide();
          }
        },
        error: (error) => {
          console.error('Failed to load charging station:', error);
          this.spinnerService.hide();
          this.connectorStatus = '';
          this.dataLoaded = false;
          observer.error(error);
        },
      });
    });
  }

  private async setupLanguagePreferences(): Promise<void> {
    this.browserLanguage = this.languageService.getBrowserLanguage() || 'fa';
    const languageToUse = 'fa';
    await firstValueFrom(this.translateService.use(languageToUse));
    this.selectedLanguage = languageToUse;
    document.documentElement.dir = 'rtl';
    document.documentElement.lang = languageToUse;
  }

}
